import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import i18n from '@/i18n';
import { isValidEmail } from '@/utils/candidate-email';
import { useCandidateEmail } from './useCandidateEmail';

export interface UseInlineEmailEditParams {
  jobId: string;
  candidateId: string;
}

/**
 * Encapsulates inline email-edit state and handlers for table identity cells.
 * Single place for save/cancel/toast logic.
 */
export function useInlineEmailEdit({ jobId, candidateId }: UseInlineEmailEditParams) {
  const { updateEmail, isUpdating: isUpdatingEmail } = useCandidateEmail({ jobId });
  const [showInlineEmail, setShowInlineEmail] = useState(false);
  const [inlineEmailValue, setInlineEmailValue] = useState('');

  const handleSave = useCallback(async () => {
    if (!inlineEmailValue.trim()) return;
    if (!isValidEmail(inlineEmailValue.trim())) {
      toast.error(i18n.t('candidates:notifications.invalid_email'));
      return;
    }
    try {
      await updateEmail({ candidateId, email: inlineEmailValue.trim() });
      setShowInlineEmail(false);
      setInlineEmailValue('');
      toast.success(i18n.t('candidates:notifications.email_updated'));
    } catch {
      toast.error(i18n.t('common:toasts.unexpected_error'));
    }
  }, [candidateId, inlineEmailValue, updateEmail]);

  const handleCancel = useCallback(() => {
    setShowInlineEmail(false);
    setInlineEmailValue('');
  }, []);

  return {
    showInlineEmail,
    setShowInlineEmail,
    inlineEmailValue,
    setInlineEmailValue,
    handleSave,
    handleCancel,
    isUpdatingEmail,
  };
}
